/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/view/view';
import Component from 'can/component/component';
import template from './photo.stache';
import 'components/modal-photo-gallery/modal-photo-gallery';
import deviceUtils, { reCheckMobile } from 'scripts/utils/device';
import url from 'scripts/utils/url';
import Swiper from 'swiper';
import dtm from 'scripts/services/dtm';
import appContext from 'scripts/app-context';
import 'dotdotdot';
import _ from 'lodash';

const googleAds = window.globalSettings.galleryAds;
import { sendPhotoGallerySwipeAnalitycs } from '../../controllers/analitycs.controller';
import { adsMap } from 'components/dfp-advert-unit/dfp-advert-unit';

const getSlideHash = (slideIndex) => `!&slide=${slideIndex}`;

Component.extend({
    tag: 'card-photo',
    template: template,
    viewModel: {
        define: {
            showCover: {
                type: 'boolean',
                value: true,
            },
            sliderClicked: {
                type: 'boolean',
                value: false,
            },
            isMobile: {
                get() {
                    return (
                        ['small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
            activeIndex: {
                type: 'number',
                value: 0,
            },
            activePaginationIndex: {
                type: 'number',
                value: 1,
            },
            gallery: {
                Type: can.List,
                Value: can.List,
            },
            tags: {
                Type: can.List,
                Value: can.List,
            },
            currentUrl: {
                type: 'string',
                value: '',
            },
            isAdsAdded: {
                type: 'boolean',
                value: false,
            },
        },
        element: null,
        openModalGallery(event, galleryTitle, galleryUrl, gallery, tags) {
            event.preventDefault();
            let topPosition;

            if (this.attr('isMobile') || deviceUtils.isSafari()) {
                topPosition = document.body.scrollTop;
            }

            this.setGalleryUrl(galleryUrl);

            can.$(
                can.stache(
                    '<modal-photo-gallery {photos}="photos" {tags}="tags" {gallery-title}="galleryTitle" {gallery-url}="galleryUrl" {active-index}="activeIndex"></modal-photo-gallery>',
                )({
                    photos: gallery,
                    tags,
                    galleryTitle,
                    galleryUrl,
                    activeIndex: this.attr('activeIndex'),
                }),
            ).appendTo(document.body);

            const modalGalleryViewModel = can
                .$('modal-photo-gallery')
                .viewModel();

            modalGalleryViewModel.bind(
                'modalGalleryClosed',
                (type, activeIndex) => {
                    this.restoreCurrentPageUrl();

                    if (this.swiper && this.swiper.slideTo) {
                        this.swiper.slideTo(activeIndex, 0);
                    }

                    modalGalleryViewModel.unbind('activeSlideIndex');
                    modalGalleryViewModel.unbind('modalGalleryClosed');
                },
            );

            modalGalleryViewModel.bind('activeSlideIndex', (type, index) => {
                const hash = getSlideHash(index);
                url.setHash(hash);
            });

            if (galleryTitle) {
                dtm.trackFeaturedTimeline(galleryTitle);
            }
        },
        setGalleryUrl(galleryUrl) {
            const galleryPageUrl = url.getUrlOfSameOrigin(galleryUrl);

            if (galleryPageUrl) {
                this.attr('currentUrl', location.href);
                url.pushState(galleryPageUrl);

                const slideIndex = this.attr('activeIndex');
                const hash = getSlideHash(slideIndex);
                url.setHash(hash);
            }
        },
        restoreCurrentPageUrl() {
            if (this.currentUrl) {
                url.pushState(this.currentUrl);
            }
        },
        isMobile() {
            return reCheckMobile();
        },
    },
    helpers: {
        getCardLink(galleryUrl) {
            const activeIndex = this.attr('activeIndex');

            const hash = getSlideHash(activeIndex);
            const shareUrl = `${galleryUrl}#${hash}`;
            return shareUrl;
        },
    },
    events: {
        inserted($el) {
            this.viewModel.attr('element', $el);
        },
        initSwiper() {
            if (this.swiper) {
                this.swiper.destroy(true, true);
                this.swiper = null;
            }
            this.initCloudinaryImage();
            this.slideCaption = this.element.find('.js-slide-caption');
            this.$swiperContainer = $(this.element).find('.swiper-container');

            if (googleAds && googleAds.id && !adsMap.get(googleAds.id)) {
                this.insertAds(googleAds);
            }

            this.swiper = new Swiper(this.$swiperContainer, {
                init: false,
                initialSlide: this.viewModel.attr('activeIndex'),
                spaceBetween: 0,
                slidesPerView: 1,
                loop: false,
                preventClicks: true,
                zoom: false,
                lazyLoading: false,
                keyboard: {
                    enabled: true,
                },
                navigation: {
                    prevEl: $(this.element).find('.swiper-button-prev'),
                    nextEl: $(this.element).find('.swiper-button-next'),
                },
            });

            this.viewModel.attr('swiper', this.swiper);
            this.swiper.on('transitionEnd', () => {
                const activeIndex = this.swiper.activeIndex;
                const galleryTitle = this.element.find('.c-card').data('title');
                const slideCaption = this.viewModel.attr(
                    `gallery.${activeIndex}.caption`,
                );
                const slideImageId = this.viewModel.attr(
                    `gallery.${activeIndex}.image.identifier`,
                );
                this.viewModel.attr('activeIndex', activeIndex);
                this.setPaginationIndex(activeIndex);
                sendPhotoGallerySwipeAnalitycs(
                    galleryTitle,
                    slideImageId,
                    slideCaption,
                    activeIndex + 1,
                );
            });

            this.swiper.on('init', () => {
                this.setPaginationIndex(this.swiper.activeIndex);
            });

            this.swiper.init();
        },
        initCloudinaryImage() {
            this.cloudinaryImage = this.element
                .find('image-cloudinary')
                .viewModel();
            this.cloudinaryImage.bind('cloudinaryImageLoaded', () => {
                _.delay(this.initSwiper.bind(this, this.element[0]), 500);
                this.cloudinaryImage.unbind('cloudinaryImageLoaded');
            });
        },
        setPaginationIndex(slideIndex) {
            if (_.isNil(slideIndex)) {
                this.viewModel.attr('activePaginationIndex', null);
                return this.slideCaption.text('');
            }

            const realIndex = this.$swiperContainer
                .find(`.swiper-slide`)
                .eq(slideIndex)
                .data('slide-id');

            if (_.isNil(realIndex)) {
                this.slideCaption.text('');
                this.viewModel.attr('activePaginationIndex', 0);
                return;
            }

            const activeSlide = this.viewModel.attr('gallery')[realIndex];
            this.viewModel.attr('activePaginationIndex', realIndex + 1);
            this.slideCaption.text(activeSlide.caption).dotdotdot();
        },
        getAdIndex(ads) {
            const adsIndex = Number(ads.slideNum) - 1;
            const galleryLength = this.viewModel.attr('gallery.length') - 1;
            return adsIndex > galleryLength ? galleryLength : adsIndex;
        },
        insertAds(ads) {
            if (!ads) {
                return;
            }

            const index = this.getAdIndex(ads);
            if (this.viewModel.attr('isAdsAdded')) {
                return;
            }

            const view = can.stache(
                `
                <div class='swiper-dfp-container'>
                    <dfp-advert-unit
                        reload-interval='{{reloadInterval}}'
                        ad-unit-path='{{adUnitPath}}'
                        {sizes}='sizes'
                        {responsive}='responsive'
                        (didnotrender)="onAdDidNotRender()"
                    />
                </div>
                        `,
            )({
                sizes: ads.sizes,
                adUnitPath: ads.id,
                responsive: true,
                reloadInterval: 5,
                onAdDidNotRender: () => this.removeAds(),
            });

            can.$(`<div class='swiper-slide ad' />`)
                .append(view)
                .insertAfter(
                    this.$swiperContainer.find('.swiper-slide').eq(index),
                );

            this.viewModel.attr('isAdsAdded', true);
        },
        removeAds() {
            if (!this.viewModel.attr('isAdsAdded')) {
                return;
            }

            const index = this.$swiperContainer
                .find('.swiper-slide.ad')
                .index();
            this.swiper.removeSlide(index);
            this.swiper.update();
            this.swiper.updateSlides();
            this.setPaginationIndex(this.swiper.activeIndex);
            this.viewModel.attr('isAdsAdded', false);
        },
        '.js-start-gallery click'($el) {
            this.viewModel.attr('sliderClicked', true);
            if (this.viewModel.attr('showCover')) {
                if (
                    $el.hasClass(
                        's-photo-gallery__button s-photo-gallery__button--next',
                    )
                ) {
                    let photosLength = this.viewModel.attr('gallery').length;
                    this.viewModel.attr(
                        'activeIndex',
                        photosLength > 0 ? photosLength - 1 : 0,
                    );
                }
            }

            this.viewModel.attr('showCover', false);
            this.initSwiper();
        },
        'a.js-preview-img click'() {
            this.viewModel.attr('showCover', false);
            this.initSwiper();
        },
    },
});
